import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Building2, FileText, Landmark, Scale, Heart, CheckCircle, Star, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { publicApi } from '../lib/api';
import { useLanguage } from '../hooks/useLanguage';
import { useCMSContent } from '../hooks/useCMSContent';
import SEO from '../components/SEO';

const HomePage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const { t, language } = useLanguage();
  
  // Fetch CMS content with fallback
  const { blocks, getBlock } = useCMSContent('home');

  // Helper to get CMS content with language support, falling back to translations
  const getCMSText = useCallback((blockKey, field, translationKey) => {
    const block = blocks[blockKey];
    if (block && Object.keys(block).length > 0) {
      const langField = language === 'es' ? `${field}_es` : field;
      return block[langField] || block[field] || t(translationKey);
    }
    return t(translationKey);
  }, [blocks, language, t]);

  // Get "why choose us" cards from CMS or defaults - memoized
  const whyChooseCards = useMemo(() => {
    const block = blocks['why_choose'];
    if (block && block.cards && block.cards.length > 0) {
      return block.cards.map((card, index) => ({
        number: card.number || String(index + 1).padStart(2, '0'),
        title: language === 'es' ? (card.title_es || card.title) : card.title,
        description: language === 'es' ? (card.description_es || card.description) : card.description,
      }));
    }
    // Default cards from translations
    return [
      { number: '01', title: t('whyChoose.card01Title'), description: t('whyChoose.card01Desc') },
      { number: '02', title: t('whyChoose.card02Title'), description: t('whyChoose.card02Desc') },
      { number: '03', title: t('whyChoose.card03Title'), description: t('whyChoose.card03Desc') },
      { number: '04', title: t('whyChoose.card04Title'), description: t('whyChoose.card04Desc') },
      { number: '05', title: t('whyChoose.card05Title'), description: t('whyChoose.card05Desc') },
    ];
  }, [blocks, language, t]);

  const services = useMemo(() => [
    {
      icon: Shield,
      title: t('services.trademarks.title'),
      description: t('services.trademarks.description'),
      href: '/services/trademark',
    },
    {
      icon: Building2,
      title: t('services.businessFormation.title'),
      description: t('services.businessFormation.description'),
      href: '/services/business-formation',
    },
    {
      icon: FileText,
      title: t('services.contracts.title'),
      description: t('services.contracts.description'),
      href: '/services/contracts',
    },
    {
      icon: Landmark,
      title: t('services.governmentContracts.title'),
      description: t('services.governmentContracts.description'),
      href: '/government-contracts',
    },
    {
      icon: Scale,
      title: t('services.personalInjury.title'),
      description: t('services.personalInjury.description'),
      href: '/services/personal-injury',
    },
    {
      icon: Heart,
      title: t('services.familyLegalPlanning.title'),
      description: t('services.familyLegalPlanning.description'),
      href: '/family-legal-planning',
    },
  ], [t]);

  const processSteps = useMemo(() => [
    {
      step: '01',
      title: t('process.step01Title'),
      description: t('process.step01Desc'),
    },
    {
      step: '02',
      title: t('process.step02Title'),
      description: t('process.step02Desc'),
    },
    {
      step: '03',
      title: t('process.step03Title'),
      description: t('process.step03Desc'),
    },
  ], [t]);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const res = await publicApi.getTestimonials();
        setTestimonials(res.data);
      } catch (error) {
        console.error('Error loading testimonials:', error);
      }
    };
    loadTestimonials();
    publicApi.trackEvent({ event_type: 'page_view', page: 'home' }).catch(() => {});
  }, []);

  const handleCtaClick = (ctaName) => {
    publicApi.trackEvent({ event_type: 'cta_click', page: 'home', metadata: { cta: ctaName } }).catch(() => {});
  };

  return (
    <div data-testid="home-page">
      <SEO 
        title="The Fine Print Lawyer - Trademark, Business & Contract Attorney"
        description="Strategic legal counsel for builders, brands, and businesses. Trademark registration, business formation, and contract services. Licensed in Georgia and Texas."
      />
      {/* Hero Section */}
      <section className="relative bg-[#FDFCFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
                {getCMSText('hero', 'badge', 'hero.badge')}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.1] text-[#222222] mb-6">
                {getCMSText('hero', 'headline', 'hero.headline')}
              </h1>
              <p className="text-lg md:text-xl text-[#555555] leading-relaxed mb-8 max-w-xl">
                {getCMSText('hero', 'subheadline', 'hero.subheadline')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to={getBlock('hero')?.cta_link || "/book"} onClick={() => handleCtaClick('hero_book')} data-testid="hero-cta-book">
                  <Button className="w-full sm:w-auto bg-[#1F2A44] hover:bg-[#151D30] text-white px-8 py-4 rounded-md text-sm font-medium">
                    {getCMSText('hero', 'cta_text', 'hero.ctaBook')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to={getBlock('hero')?.secondary_cta_link || "/services"} onClick={() => handleCtaClick('hero_services')} data-testid="hero-cta-services">
                  <Button variant="outline" className="w-full sm:w-auto border-[#1F2A44] text-[#1F2A44] hover:bg-[#1F2A44] hover:text-white px-8 py-4 rounded-md text-sm font-medium">
                    {getCMSText('hero', 'secondary_cta_text', 'hero.ctaServices')}
                  </Button>
                </Link>
              </div>
              {/* Gold accent line */}
              <div className="w-16 h-1 bg-[#C2A46D]"></div>
            </div>
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute -top-4 -right-4 w-full h-full border-2 border-[#C2A46D]/30 rounded-lg"></div>
                <img
                  src="https://customer-assets.emergentagent.com/job_lawfirm-cms-2/artifacts/rql9i2dk_photo%20dec%2030%202023%2C%2012%2045%2012%20pm%20%282%29.PNG"
                  alt="Jennifer N. Brantley - The Legal Architect"
                  className="w-full h-auto object-cover rounded-lg shadow-2xl relative z-10"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Blue Background with Gold Accents */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              {getCMSText('why_choose', 'section_label', 'whyChoose.sectionLabel')}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white">
              {getCMSText('why_choose', 'headline', 'whyChoose.headline')}
            </h2>
            <div className="w-16 h-1 bg-[#C2A46D] mx-auto mt-6"></div>
          </div>
          
          {/* Bento Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Row 1-2: Card 01 (tall) + Cards 02 & 03 stacked */}
            <div className="lg:row-span-2">
              {/* Card 01 - Strategic Perspective (Tall gold card) */}
              <div
                className="h-full p-8 md:p-10 rounded-lg bg-[#C2A46D] text-[#1F2A44] flex flex-col justify-between"
                data-testid="why-card-0"
              >
                <div>
                  <div className="text-sm font-mono mb-4 text-[#1F2A44]/60">
                    {whyChooseCards[0]?.number || '01'}
                  </div>
                  <h3 className="text-2xl md:text-3xl font-serif mb-4 text-[#1F2A44]">
                    {whyChooseCards[0]?.title}
                  </h3>
                  <p className="text-[#1F2A44]/80 leading-relaxed text-base">
                    {whyChooseCards[0]?.description}
                  </p>
                </div>
                <div className="w-12 h-1 bg-[#1F2A44]/30 mt-8"></div>
              </div>
            </div>

            {/* Card 02 - Practical Guidance */}
            <div
              className="p-8 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
              data-testid="why-card-1"
            >
              <div className="text-sm font-mono mb-3 text-[#C2A46D]">
                {whyChooseCards[1]?.number || '02'}
              </div>
              <h3 className="text-xl font-serif mb-3 text-white">
                {whyChooseCards[1]?.title}
              </h3>
              <p className="text-white/70 text-sm">
                {whyChooseCards[1]?.description}
              </p>
            </div>

            {/* Card 03 - Structured Services */}
            <div
              className="p-8 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
              data-testid="why-card-2"
            >
              <div className="text-sm font-mono mb-3 text-[#C2A46D]">
                {whyChooseCards[2]?.number || '03'}
              </div>
              <h3 className="text-xl font-serif mb-3 text-white">
                {whyChooseCards[2]?.title}
              </h3>
              <p className="text-white/70 text-sm">
                {whyChooseCards[2]?.description}
              </p>
            </div>

            {/* Card 04 - Experience */}
            <div
              className="p-8 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
              data-testid="why-card-3"
            >
              <div className="text-sm font-mono mb-3 text-[#C2A46D]">
                {whyChooseCards[3]?.number || '04'}
              </div>
              <h3 className="text-xl font-serif mb-3 text-white">
                {whyChooseCards[3]?.title}
              </h3>
              <p className="text-white/70 text-sm">
                {whyChooseCards[3]?.description}
              </p>
            </div>

            {/* Card 05 - Responsiveness */}
            <div
              className="p-8 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20"
              data-testid="why-card-4"
            >
              <div className="text-sm font-mono mb-3 text-[#C2A46D]">
                {whyChooseCards[4]?.number || '05'}
              </div>
              <h3 className="text-xl font-serif mb-3 text-white">
                {whyChooseCards[4]?.title}
              </h3>
              <p className="text-white/70 text-sm">
                {whyChooseCards[4]?.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white border-y border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              {getCMSText('services_preview', 'section_label', 'services.sectionLabel')}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#222222]">
              {getCMSText('services_preview', 'headline', 'services.headline')}
            </h2>
            <div className="w-16 h-1 bg-[#C2A46D] mx-auto mt-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.href}
                className="group relative bg-[#FDFCFA] p-8 md:p-10 border border-[#E8E4DC] rounded-lg hover:border-[#C2A46D]/50 hover:shadow-xl transition-all duration-500"
                data-testid={`service-card-${index}`}
              >
                <service.icon className="w-10 h-10 text-[#1F2A44] mb-6" />
                <h3 className="text-xl font-serif text-[#222222] mb-3 group-hover:text-[#1F2A44] transition-colors">
                  {service.title}
                </h3>
                <p className="text-[#555555] text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <span className="inline-flex items-center text-sm font-medium text-[#C2A46D] group-hover:gap-2 transition-all">
                  {t('services.learnMore')}
                  <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-[#FDFCFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              {t('process.sectionLabel')}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#222222]">
              {t('process.headline')}
            </h2>
            <div className="w-16 h-1 bg-[#C2A46D] mx-auto mt-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center" data-testid={`process-step-${index}`}>
                <div className="w-16 h-16 mx-auto mb-6 bg-[#1F2A44] text-white rounded-full flex items-center justify-center text-2xl font-serif border-2 border-[#C2A46D]">
                  {step.step}
                </div>
                <h3 className="text-xl font-serif text-[#222222] mb-3">
                  {step.title}
                </h3>
                <p className="text-[#555555] text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white border-y border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              {t('testimonials.sectionLabel')}
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#222222]">
              {t('testimonials.headline')}
            </h2>
            <div className="w-16 h-1 bg-[#C2A46D] mx-auto mt-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.length > 0 ? testimonials.slice(0, 4).map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="bg-[#FDFCFA] p-8 md:p-10 rounded-lg border-l-4 border-[#C2A46D]"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#C2A46D] text-[#C2A46D]" />
                  ))}
                </div>
                <p className="text-[#222222] leading-relaxed mb-6 italic text-lg">
                  "{testimonial.content}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#1F2A44] rounded-full flex items-center justify-center text-white font-semibold">
                    {testimonial.author_name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-[#222222]">{testimonial.author_name}</p>
                    {testimonial.author_title && (
                      <p className="text-sm text-[#555555]">{testimonial.author_title}</p>
                    )}
                  </div>
                </div>
              </div>
            )) : (
              [
                { name: "Marcus Thompson", title: "Founder, Thompson Creative Agency", content: "Jennifer made the trademark process incredibly smooth. She explained everything clearly and filed our application with precision. Our brand is now fully protected." },
                { name: "Aisha Williams", title: "CEO, Brightpath Consulting", content: "From business formation to our first set of client contracts, Jennifer was there every step of the way. Her attention to detail saved us from several potential pitfalls." },
                { name: "David Chen", title: "Owner, Pacific Coast Designs", content: "I needed help navigating a government contract opportunity, and Jennifer's expertise was invaluable. She guided me through the entire process." },
                { name: "Rachel Foster", title: "Founder, Foster & Co. Beauty", content: "Jennifer helped us structure our business properly from the start. Her legal infrastructure bundle gave us everything we needed to launch with confidence." }
              ].map((testimonial, index) => (
                <div
                  key={index}
                  className="bg-[#FDFCFA] p-8 md:p-10 rounded-lg border-l-4 border-[#C2A46D]"
                  data-testid={`testimonial-placeholder-${index}`}
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-[#C2A46D] text-[#C2A46D]" />
                    ))}
                  </div>
                  <p className="text-[#222222] leading-relaxed mb-6 italic text-lg">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#1F2A44] rounded-full flex items-center justify-center text-white font-semibold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-[#222222]">{testimonial.name}</p>
                      <p className="text-sm text-[#555555]">{testimonial.title}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
            {t('cta.sectionLabel')}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6">
            {getCMSText('cta_band', 'headline', 'cta.headline')}
          </h2>
          <div className="w-16 h-1 bg-[#C2A46D] mx-auto mb-8"></div>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            {getCMSText('cta_band', 'description', 'cta.subheadline')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={getBlock('cta_band')?.cta_link || "/book"} onClick={() => handleCtaClick('cta_book')} data-testid="cta-book-btn">
              <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-10 py-4 rounded-md text-sm font-semibold">
                {getCMSText('cta_band', 'cta_text', 'cta.ctaBook')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact" onClick={() => handleCtaClick('cta_contact')} data-testid="cta-contact-btn">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#1F2A44] px-10 py-4 rounded-md text-sm font-medium">
                {t('cta.ctaContact')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
